import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import { getSEOConfig, updateSEOTags } from '../../i18n/utils/seo';
import type { SupportedLanguage } from '../../i18n/utils/language';

export default function YouTubeTimestampLink() {
  const { t, i18n } = useTranslation(['youtube', 'common']);

  // State
  const [videoUrl, setVideoUrl] = useState('');
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState('');
  const [copySuccess, setCopySuccess] = useState('');

  // Update SEO
  useEffect(() => {
    const currentLang = i18n.language as SupportedLanguage;
    const seoConfig = getSEOConfig('youtubeLink', currentLang);
    updateSEOTags(seoConfig, currentLang);
    document.documentElement.lang = currentLang;
  }, [i18n.language]);

  // Extract video ID from URL
  const extractVideoId = (url: string): string | null => {
    if (!url) return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }

    return null;
  };

  // Calculate total seconds from HH:MM:SS
  const calculateTotalSeconds = (): number => {
    return hours * 3600 + minutes * 60 + seconds;
  };

  // Update time inputs when total seconds changes
  const handleTotalSecondsChange = (value: string) => {
    setTotalSeconds(value);
    const secs = parseInt(value) || 0;
    setHours(Math.floor(secs / 3600));
    setMinutes(Math.floor((secs % 3600) / 60));
    setSeconds(secs % 60);
  };

  // Update total seconds when time inputs change
  useEffect(() => {
    const total = calculateTotalSeconds();
    setTotalSeconds(String(total));
  }, [hours, minutes, seconds]);

  const videoId = extractVideoId(videoUrl);
  const startTime = calculateTotalSeconds();

  // Generate links
  const shortLink = videoId ? `https://youtu.be/${videoId}?t=${startTime}` : '';
  const fullLink = videoId ? `https://www.youtube.com/watch?v=${videoId}&t=${startTime}s` : '';
  const embedCode = videoId ? `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}?start=${startTime}" frameborder="0" allowfullscreen></iframe>` : '';

  // Copy to clipboard
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 py-4 md:py-8">
        {/* Page Header */}
        <div className="text-center mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            {t('link.mainTitleSEO')}
          </h1>
          <p className="text-lg text-gray-600">{t('link.subtitle')}</p>
        </div>

        {/* Link Generator Tool */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
            {t('link.generatorTitle')}
          </h2>

          <div className="space-y-6">
            {/* Video URL Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('link.pasteUrl')}
              </label>
              <input
                type="url"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder={t('link.urlPlaceholder')}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all"
              />
              {videoId && (
                <p className="mt-2 text-sm text-green-600">✓ Video ID: {videoId}</p>
              )}
            </div>

            {/* Time Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('link.startTime')}
              </label>
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('link.hours')}</label>
                  <input
                    type="number"
                    min="0"
                    value={hours}
                    onChange={(e) => setHours(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-red-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('link.minutes')}</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={minutes}
                    onChange={(e) => setMinutes(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-red-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('link.seconds')}</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={seconds}
                    onChange={(e) => setSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-red-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">{t('link.orEnterSeconds')}</label>
                <input
                  type="number"
                  min="0"
                  value={totalSeconds}
                  onChange={(e) => handleTotalSecondsChange(e.target.value)}
                  placeholder={t('link.secondsPlaceholder')}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-red-500"
                />
              </div>
            </div>

            {/* Generated Links */}
            {videoId && (
              <div className="space-y-4 pt-4 border-t-2 border-gray-200">
                <h3 className="text-lg font-bold text-gray-800">{t('link.generatedLinks')}</h3>

                {/* Short Link */}
                <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-4 border border-red-200">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('link.shortLink')}
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-white rounded border border-red-300 text-sm break-all">
                      {shortLink}
                    </code>
                    <button
                      onClick={() => copyToClipboard(shortLink, 'short')}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors whitespace-nowrap"
                    >
                      {copySuccess === 'short' ? t('common:button.copied') : t('link.copyLink')}
                    </button>
                  </div>
                </div>

                {/* Full Link */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-300">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('link.fullLink')}
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-white rounded border border-gray-300 text-sm break-all">
                      {fullLink}
                    </code>
                    <button
                      onClick={() => copyToClipboard(fullLink, 'full')}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors whitespace-nowrap"
                    >
                      {copySuccess === 'full' ? t('common:button.copied') : t('link.copyLink')}
                    </button>
                  </div>
                </div>

                {/* Embed Code */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-300">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('link.embedCode')}
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-900 text-green-400 rounded text-xs break-all font-mono">
                      {embedCode}
                    </code>
                    <button
                      onClick={() => copyToClipboard(embedCode, 'embed')}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors whitespace-nowrap"
                    >
                      {copySuccess === 'embed' ? t('common:button.copied') : t('link.copyEmbed')}
                    </button>
                  </div>
                </div>

                {/* Preview */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('link.preview')}
                  </label>
                  <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?start=${startTime}`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* URL Formats */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
            {t('link.urlFormats')}
          </h2>

          <div className="space-y-6">
            {[1, 2, 3, 4].map((num) => (
              <div key={num} className="border-l-4 border-red-500 pl-4">
                <h3 className="font-bold text-gray-800 mb-2">{t(`link.format${num}Title`)}</h3>
                <p className="text-gray-600 mb-2">{t(`link.format${num}Desc`)}</p>
                {num === 1 && (
                  <ul className="text-sm text-gray-600 space-y-1 ml-4">
                    <li>• {t('link.format1Example1')}</li>
                    <li>• {t('link.format1Example2')}</li>
                    <li>• {t('link.format1Example3')}</li>
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Use Cases */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
            {t('link.useCases')}
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <div key={num} className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-5 border border-red-200">
                <h3 className="font-semibold text-gray-800 mb-2">{t(`link.useCase${num}Title`)}</h3>
                <p className="text-sm text-gray-600">{t(`link.useCase${num}Desc`)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How to Use */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
            {t('link.howToUse')}
          </h2>

          <div className="space-y-4">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-red-100 text-red-600 rounded-full text-sm font-bold flex-shrink-0">
                  {step}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">{t(`link.step${step}`)}</h3>
                  <p className="text-gray-600">{t(`link.step${step}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pro Tips */}
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8 border-2 border-yellow-200">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">
            {t('link.tips')}
          </h2>
          <div className="space-y-2 text-gray-700">
            {[1, 2, 3, 4].map((num) => (
              <p key={num}>{t(`link.tip${num}`)}</p>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
            {t('link.faq')}
          </h2>

          <div className="space-y-4">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <details key={num} className="group">
                <summary className="flex justify-between items-center cursor-pointer list-none p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                  <span className="font-semibold text-gray-800">{t(`link.faq.q${num}`)}</span>
                  <span className="transition group-open:rotate-180">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <div className="px-4 py-3 text-gray-600">
                  {t(`link.faq.a${num}`)}
                </div>
              </details>
            ))}
          </div>
        </div>

        {/* Related Tools */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">
            {t('link.relatedTools')}
          </h2>

          <div className="space-y-4">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5">
              <h3 className="font-semibold text-gray-800 mb-2">{t('link.needChapters')}</h3>
              <p className="text-gray-600 mb-3">{t('link.needChaptersDesc')}</p>
              <a
                href="/youtube-timestamp-generator"
                className="inline-block bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg transition-colors"
              >
                {t('link.checkGenerator')} →
              </a>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5">
              <h3 className="font-semibold text-gray-800 mb-2">{t('link.needUnixTime')}</h3>
              <p className="text-gray-600 mb-3">{t('link.needUnixTimeDesc')}</p>
              <a
                href="/timestamp-converter"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg transition-colors"
              >
                {t('common:nav.timestamp')} →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
